/**
 * training-groups-context.js
 */

import React, { createContext } from 'react';
import axios from 'axios';
import { UserContext } from 'contexts/user/user-context';
import ConfirmDialog from 'components/confirm-dialog';
import sortMembers from 'components/groups/sort-members';


export const TrainingGroupsContext = createContext();


const sortGroups = (user) => (a, b) => {
  // handler && is member
  if (user.role.isHandler && Boolean(a.membership?.dateAccepted) !== Boolean(b.membership?.dateAccepted)) {
    return Boolean(a.membership?.dateAccepted) ? -1 : 1;
  }
  // trainer and just me at bottom
  else if (user.role.isTrainer && a.name.startsWith('Just Me') && !b.name.startsWith('Just Me')) {
    return 1;
  } else if (user.role.isTrainer && !a.name.startsWith('Just Me') && b.name.startsWith('Just Me')) {
    return -1;
  }
  // name asc
  else {
    return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
  }
}


export const TrainingGroupsProvider = (props) => {
  const {
    groups,
    children
  } = props;
  const { user } = React.useContext(UserContext);
  const [action, setAction] = React.useState();
  const [confirmAction, setConfirmAction] = React.useState();


  // PRIVATE: ON UPDATE GROUP
  const onUpdateGroup = (group, isNew) => {
    const newGroup = {
      ...group,
      members: group.members?.sort(sortMembers(user))
    };
    setPublicState(p => {
      const newGroups = isNew
        ? [...p.groups, newGroup]
        : p.groups.map(g => g.id === group.id ? newGroup : g);
      return { ...p, groups: newGroups.sort(sortGroups(user)) };
    });
  }


  // PRIVATE: ON REMOVE GROUP
  const onRemoveGroup = (group) => {
    setPublicState(p => {
      const newGroups = p.groups.filter(g => g.id !== group.id);
      return { ...p, groups: newGroups.sort(sortGroups(user)) };
    });
  }


  // PRIVATE: DO REQUEST
  const doRequest = async (url, config, setIsLoading) => {
    let response = null;
    setIsLoading && setIsLoading(true);
    try {
      response = await axios(url, config);
    } finally {
      setIsLoading && setIsLoading(false);
    }
    return response;
  }


  // PUBLIC: ON CREATE GROUP REQUEST
  const onCreateRequest = async (groupCode, setIsLoading) => {
    // loading indication is handled within the dialog
    if (!groupCode) {
      return 'Please enter a group code';
    }

    try {
      const response = await doRequest(`/groups/${groupCode}/member-request`, { method: 'post', bypassErrorHandling: true }, setIsLoading);
      if (response.status === 200) {
        onUpdateGroup(response.data, true);
        return true;
      }
      return response?.data || 'Unknown error';
    } catch (e) {
      return e?.response?.data?.Message || e?.response?.data || e?.message || 'Unknown error';
    }
  }


  // PUBLIC: ON DELETE GROUP REQUEST
  const onDeleteRequest = async (group, code, setIsLoading) => {
    setAction({ action: 'remove this member request for', title: 'Remove', group });
    if (!(await confirmAction())) {
      return false;
    }

    const response = await doRequest(`/groups/member-request/${code}`, { method: 'delete' }, setIsLoading);
    if (response.status === 200) {
      onRemoveGroup(group);
      return true;
    }
    return false;
  }


  // PUBLIC: ON ACCEPT GROUP REQUEST
  const onAcceptRequest = async (group, code, setIsLoading) => {
    const response = await doRequest(`/groups/member-request/${code}`, { method: 'post' }, setIsLoading);
    if (response.status === 200) {
      onUpdateGroup(response.data);
      return true;
    }
    return false;
  }


  // PUBLIC: ON SAVE GROUP
  const onSaveGroup = async (group, setIsLoading) => {
    const url = group.id ? `/groups/${group.id}` : `/groups`;
    const config = { method: group.id ? 'put' : 'post', data: group };
    const response = await doRequest(url, config, setIsLoading);
    if (response.status === 200) {
      if (response.data) {
        onUpdateGroup(response.data, !group.id);
      } else {
        // user removed themselves from group
        onRemoveGroup(group);
      }
    }
    return response;
  }


  // PUBLIC: ON LEAVE GROUP
  const onLeaveGroup = async (group, setIsLoading) => {
    setAction({ action: 'leave', title: 'Leave', group });
    if (!(await confirmAction())) {
      return false;
    }

    const response = await doRequest(`/groups/${group.id}/membership`, { method: 'delete' }, setIsLoading);
    if (response.status === 200) {
      onRemoveGroup(group);
      return true;
    }
    return false;
  }


  // PUBLIC: ON DELETE GROUP
  const onDeleteGroup = async (group, setIsLoading) => {
    setAction({ action: 'delete', title: 'Delete', group });
    if (!(await confirmAction())) {
      return false;
    }

    const response = await doRequest(`/groups/${group.id}`, { method: 'delete' }, setIsLoading);
    if (response.status === 200) {
      onRemoveGroup(group);
      return true;
    }
    return false;
  }


  // PUBLIC INTERFACE CONTAINER
  const [publicState, setPublicState] = React.useState({
    // sort groups and members when initializing
    groups: [...groups]
      .map(g => ({
        ...g,
        name: g.name === 'Just Me' && g.members?.length === 1 ? `Just Me (${g.members[0].name})` : g.name,
        members: g.members?.sort(sortMembers(user))
      }))
      .sort(sortGroups(user)),
    onCreateRequest,
    onDeleteRequest,
    onAcceptRequest,
    onSaveGroup,
    onLeaveGroup,
    onDeleteGroup,
  });


  // UPDATE PUBLIC STATE WHEN CONFIRM ACTION IS SET
  // TODO: consider using ref for confirmAction instead of state var
  React.useEffect(() => {
    setPublicState(p => ({
      ...p,
      onCreateRequest,
      onDeleteRequest,
      onAcceptRequest,
      onSaveGroup,
      onLeaveGroup,
      onDeleteGroup,
    }));
  // the public methods change on every render so including them would constantly be triggering this effect.
  // in reality, we only want to trigger this when confirmAction changes, recognizing that
  // each of the public functions validity is only tied to changes of the confirmAction state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmAction]);

  
  return (<>
    <TrainingGroupsContext.Provider value={publicState}>
      {children}
    </TrainingGroupsContext.Provider>

    <ConfirmDialog
      titleStyle="warning"
      title={`Confirm ${action?.title} Group`}
      titleIcon='warning'
      content={<>Are you sure you want to {action?.action} the training group <b>{action?.group.name}</b>?</>}
      setConfirm={setConfirmAction}
    />
  </>);
}